import { PERMISSIONS, roleKeySchema, type Permission, type RoleDto } from '@cartly/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Modal,
  Spinner,
  TextField,
  useConfirm,
  useToast,
} from '@cartly/ui';
import { Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../api/client';
import { useLoader, useSubmit } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';

/** Groups permission keys by module prefix for a readable editor. */
const PERMISSION_GROUPS = Object.entries(PERMISSIONS).reduce<
  Record<string, [Permission, string][]>
>((groups, [key, description]) => {
  const group = key.split(':')[0]!;
  (groups[group] ??= []).push([key as Permission, description]);
  return groups;
}, {});

const groupLabel = (g: string) => g.replace('_', ' ').replace(/^\w/, (c) => c.toUpperCase());

function RoleEditor({
  role,
  grantable,
  onClose,
  onSaved,
}: {
  role: RoleDto | null;
  grantable: ReadonlySet<Permission>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const submit = useSubmit();
  const [name, setName] = useState(role?.name ?? '');
  const [key, setKey] = useState(role?.key ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [selected, setSelected] = useState<Set<Permission>>(new Set(role?.permissions ?? []));

  const toggle = (p: Permission) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!role) {
      const parsedKey = roleKeySchema.safeParse(key);
      if (!parsedKey.success) {
        submit.setFieldErrors({ key: parsedKey.error.issues[0]!.message });
        return;
      }
    }
    const permissions = [...selected];
    const ok = await submit.run(async () => {
      if (role) await api.patch(`/roles/${role.id}`, { name, description, permissions });
      else await api.post('/roles', { key, name, description, permissions });
      return true;
    });
    if (!ok) return;
    toast.success(role ? 'Role updated' : 'Role created');
    onSaved();
    onClose();
  };

  return (
    <Modal
      open
      title={role ? `Edit ${role.name}` : 'New role'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="role-form" loading={submit.pending}>
            {role ? 'Save role' : 'Create role'}
          </Button>
        </>
      }
    >
      <form id="role-form" className="stack" onSubmit={onSubmit} noValidate>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        <div className="form-grid">
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={submit.fieldErrors['name']}
          />
          <TextField
            label="Key"
            value={key}
            disabled={Boolean(role)}
            onChange={(e) => setKey(e.target.value.toLowerCase())}
            hint="Lowercase identifier, e.g. support-lead"
            error={submit.fieldErrors['key']}
          />
          <TextField
            label="Description"
            fieldClassName="form-grid--full"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="permission-groups">
          {Object.entries(PERMISSION_GROUPS).map(([group, perms]) => (
            <fieldset key={group} className="permission-group">
              <legend className="permission-group__title">{groupLabel(group)}</legend>
              {perms.map(([p, desc]) => (
                <Checkbox
                  key={p}
                  label={desc}
                  checked={selected.has(p)}
                  disabled={!grantable.has(p)}
                  onChange={() => toggle(p)}
                />
              ))}
            </fieldset>
          ))}
        </div>
        <p className="text-sm text-muted">You can only grant permissions you have yourself.</p>
      </form>
    </Modal>
  );
}

export function RolesSettings() {
  const session = useActiveSession();
  const { can } = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const manage = can('roles:manage');
  const [editing, setEditing] = useState<RoleDto | 'new' | null>(null);
  const grantable = new Set(session.activeTenant?.permissions ?? []);

  const { data, error, loading, reload } = useLoader(
    () => api.get<{ items: RoleDto[] }>('/roles').then((r) => r.items),
    session.activeTenant?.id,
  );

  if (loading && !data) return <Spinner size="lg" label="Loading" />;
  if (error || !data) return <Alert tone="critical">{error ?? 'Failed to load'}</Alert>;

  const remove = async (role: RoleDto) => {
    const ok = await confirm({
      title: `Delete the ${role.name} role?`,
      message: 'Members with this role lose its permissions.',
      confirmLabel: 'Delete role',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/roles/${role.id}`);
      toast.success('Role deleted');
      await reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <>
      <Card
        title="Roles"
        subtitle="Built-in roles cover most teams. Create custom roles for anything more specific."
        actions={
          manage && (
            <Button variant="primary" icon={<Plus size={16} />} onClick={() => setEditing('new')}>
              New role
            </Button>
          )
        }
        flush
      >
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Permissions</th>
                <th>Members</th>
                <th className="table__actions">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="table__primary">
                      {r.name}{' '}
                      {r.isSystem ? <Badge>Built-in</Badge> : <Badge tone="primary">Custom</Badge>}
                    </div>
                    <div className="table__secondary">{r.description}</div>
                  </td>
                  <td className="text-muted">
                    {r.permissions.length} of {Object.keys(PERMISSIONS).length}
                  </td>
                  <td>{r.memberCount}</td>
                  <td className="table__actions">
                    {manage && !r.isSystem && (
                      <div className="row row--end">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => void remove(r)}>
                          Delete
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {editing && (
        <RoleEditor
          role={editing === 'new' ? null : editing}
          grantable={grantable}
          onClose={() => setEditing(null)}
          onSaved={() => void reload()}
        />
      )}
    </>
  );
}
